import { faL } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingAnimation from "../LoadingAnimation";

interface Url {
    bodyTitle: string;
    bodyText: string;
}

interface Newsletter {
    recipient: 'general' | 'kickstarter' | 'test';
    subject: string;
    bodyTitle: string;
    bodyText: string;
}

export default function NewsletterTool({csrfToken}:any){
    const [confirmSend, setConfirmSend] = useState<boolean>(false)
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [newsletterData, setNewsletterData] = useState<Newsletter>({
        recipient: 'test',
        subject: '',
        bodyTitle: '',
        bodyText: ''
    })
    const [urlParams, setUrlParams] = useState<Url>({
        bodyTitle: 'body title',
        bodyText: 'body text'
    })
    const [previewUrl, setPreviewUrl] = useState("")
    const [photoFile, setPhotoFile] = useState<File | null>(null)

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setPhotoFile(file)
        }
    }

    // Update the iframe URL whenever urlParams change
    useEffect(() => {
        const encodedTitle = encodeURIComponent(urlParams.bodyTitle)
        const encodedText = encodeURIComponent(urlParams.bodyText)
        const url = `${process.env.REACT_APP_SKILLJA_URL}/newsletter/get_newsletter_preview/?body_title=${encodedTitle}&body_text=${encodedText}`
        setPreviewUrl(url)
    }, [urlParams])

    function sendNewsletter(){
        setSubmitting(true)
        const formData = new FormData()
        formData.append("recipient", newsletterData.recipient)
        formData.append("subject", newsletterData.subject)
        formData.append("bodyTitle", newsletterData.bodyTitle)
        formData.append("bodyText", newsletterData.bodyText)

        if (photoFile) {
            formData.append("filepath", photoFile)
        }

        axios.post(`${process.env.REACT_APP_SKILLJA_URL}/newsletter/send_newsletter/`, formData, {
            headers: {
                "X-CSRFToken": csrfToken,
            },
            withCredentials: true,
        })
        .then((res) => {
            if (res.status !== 200) {
                alert('Something went wrong!')
            }
        })
        .catch((error) => { 
            console.error(error)
        })
        .finally(()=>{
            setSubmitting(false)
        })
    }

    return (
        <div className="flex flex-wrap my-8 border-t-4 border-main-grey-200 py-8">
            <section className="flex w-72 md:w-96 lg:w-6/12">
                {submitting ?
                    <LoadingAnimation />
                :
                    <form className="flex flex-col w-full">
                        <div className="flex flex-wrap mb-4">
                            <div className="flex mr-6">
                                <input 
                                    type="radio" 
                                    id="general" 
                                    name="newsletter" 
                                    value="general" 
                                    className="mr-2 text-sm lg:text-base" 
                                    onChange={() =>
                                        setNewsletterData((prev) => ({ ...prev, recipient: 'general' }))
                                    }
                                    required 
                                />
                                <label htmlFor="general">General Newsletter</label>
                            </div>
                            <div className="flex mr-6">
                                <input 
                                    type="radio" 
                                    id="kickstarter" 
                                    name="newsletter" 
                                    value="kickstarter" 
                                    className="mr-2 text-sm lg:text-base" 
                                    onChange={() =>
                                        setNewsletterData((prev) => ({ ...prev, recipient: 'kickstarter' }))
                                    }
                                    required 
                                />
                                <label htmlFor="kickstarter">KickStarter</label>
                            </div>
                            <div className="flex">
                                <input 
                                    type="radio" 
                                    id="test" 
                                    name="newsletter" 
                                    value="test" 
                                    className="mr-2 text-sm lg:text-base" 
                                    onChange={() =>
                                        setNewsletterData((prev) => ({ ...prev, recipient: 'test' }))
                                    }
                                    required 
                                />
                                <label htmlFor="test">Test</label>
                            </div>
                        </div>
                        <div className="flex flex-wrap lg:flex-nowrap">
                            <input
                                type="text"
                                className="form-input my-1.5 p-2.5 rounded-lg lg:mr-6"
                                placeholder="Email Subject"
                                id="subject"
                                name="subject"
                                value={newsletterData.subject}
                                onChange={(e) =>
                                    setNewsletterData((prev) => ({ ...prev, subject: e.target.value }))
                                }
                                required
                            />
                            <input
                                type="text"
                                className="form-input my-1.5 p-2.5 rounded-lg"
                                placeholder="Body Title"
                                id="body-title"
                                name="body-title"
                                value={newsletterData.bodyTitle}
                                onChange={(e) =>
                                    setNewsletterData((prev) => ({ ...prev, bodyTitle: e.target.value }))
                                }
                                required
                            />
                        </div>
                        <textarea
                            className="form-input my-1.5 p-2.5 rounded-lg min-h-40 max-h-40"
                            placeholder="Body Text"
                            id="body-text"
                            name="body-text"
                            value={newsletterData.bodyText}
                            onChange={(e) =>
                                setNewsletterData((prev) => ({ ...prev, bodyText: e.target.value }))
                            }
                            required
                        />
                        <input
                            type="file" 
                            id="photo" 
                            name="photo" 
                            accept="image/png, image/jpeg" 
                            className="my-1.5 w-fit"
                            onChange={handleFileChange}
                        />
                        <div className="flex">
                            <button 
                                type="button"
                                className="form-btn pt-2 bg-main-grey-200 px-6 w-fit mt-8 mr-4" 
                                onClick={() =>
                                    setUrlParams((prev) => ({ ...prev, bodyText: newsletterData?.bodyText, bodyTitle: newsletterData?.bodyTitle }))}
                                >
                                    Preview
                            </button>
                            {!confirmSend ?
                                <button type="button" className="form-btn pt-2 px-6 w-fit mt-8" onClick={()=>setConfirmSend(true)}>
                                    Send
                                </button>
                            :
                                <button type="button" className="form-btn bg-main-green-700 hover:bg-main-green-900 pt-2 px-6 w-fit mt-8" onClick={()=>sendNewsletter()}>
                                    Confirm Send
                                </button>
                            }
                        </div>
                    </form>
                }
            </section>
            <section className="flex mx-auto mt-10 lg:mt-0 lg:ml-auto lg:mr-0 w-80 lg:w-4/12 xl:w-5/12 border border-main-grey-100 bg-white rounded-xl">
                <iframe
                    key={previewUrl}
                    title="Preview"
                    src={previewUrl}
                    style={{ width: "100%", height: "500px", border: "none", borderRadius: "12px" }}
                />
            </section>
        </div>
    )
}